
// client/src/components/TaskList.jsx
// Standalone task list page — shows all tasks associated with the current user
// up to one year old. Managers/admins can see all tenant tasks.

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getSessionLocal, sessionRequestConfig } from "../services/sessionLocal";
import { useTranslation } from "react-i18next";
import AppFooter from "./AppFooter";
import SearchFilterBar from "./shared/SearchFilterBar";
import { FaArrowLeft, FaHome, FaCalendarAlt, FaFileCsv, FaCopy } from "react-icons/fa";
import { RefreshButton } from "./common/IconButton";
import "./TaskList.css";

const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

/** Which duedatetime/taskdate bucket does a task fall into? */
function agendaBucket(task) {
  const raw = task.duedatetime || task.taskdate || task.date;
  if (!raw) return "later";
  const d = new Date(String(raw).includes("T") || String(raw).includes("Z") ? raw : String(raw).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "later";
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday = new Date(startOfToday.getTime() + 86400000);
  const endOfWeek = new Date(startOfToday.getTime() + 7 * 86400000);
  const done = (task.status || "").toLowerCase() === "completed";
  if (!done && d < startOfToday) return "overdue";
  if (d < endOfToday) return "today";
  if (d < endOfWeek) return "week";
  return "later";
}

const STATUS_OPTIONS = [
  { value: "",            label: "All Statuses" },
  { value: "active",      label: "Active" },
  { value: "pending",     label: "Pending" },
  { value: "in-progress", label: "In Progress" },
  { value: "completed",   label: "Completed" },
  { value: "blocked",     label: "Blocked" },
];

const PRIORITY_OPTIONS = [
  { value: "",       label: "All Priorities" },
  { value: "urgent", label: "Urgent" },
  { value: "high",   label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low",    label: "Low" },
];

function formatDate(value) {
  if (!value) return "\u2014";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return String(value);
  }
}

function statusBadgeClass(status) {
  switch ((status || "").toLowerCase()) {
    case "completed":   return "bg-success";
    case "in-progress": return "bg-info";
    case "blocked":     return "bg-danger";
    default:            return "bg-secondary";
  }
}

function priorityBadgeClass(priority) {
  switch ((priority || "").toLowerCase()) {
    case "urgent": return "bg-danger";
    case "high":   return "bg-warning";
    case "medium": return "bg-info";
    default:       return "bg-secondary";
  }
}

export default function TaskList() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const session = getSessionLocal();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // View: "list" | "agenda" | "team"
  const [view, setView] = useState("list");
  const [workload, setWorkload] = useState([]);
  const [workloadLoading, setWorkloadLoading] = useState(false);

  // Calendar feed subscription
  const [feed, setFeed] = useState({ url: null, loading: false });
  const [feedCopied, setFeedCopied] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");

  // Role-based visibility
  const role = (session.role || "").toLowerCase();
  const isManagerOrAbove = ["owner", "admin", "manager"].includes(role);

  // Read dashboard config for manager visibility setting
  const managersCanSeeAll = (() => {
    try {
      const raw = localStorage.getItem("dashboardTaskConfig");
      if (raw) return JSON.parse(raw).managersCanSeeAllTasks !== false;
    } catch { /* ignore */ }
    return true;
  })();
  const canSeeAllTasks = isManagerOrAbove && managersCanSeeAll;

  const fetchTasks = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const cfg = sessionRequestConfig();
      const params = { range: "year" };

      if (!canSeeAllTasks) {
        params.username = session.username;
      }

      // Pass date range if user set one (overrides the year range on the backend)
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo)   params.dateTo = dateTo;

      // Pass status filter
      if (statusFilter) params.status = statusFilter;

      const res = await axios.get(
        `${baseUrl}/api/events/${tenant}/tasks`,
        { ...cfg, params: { ...cfg.params, ...params } }
      );

      if (res.data?.success) {
        setTasks(res.data.tasks || []);
      } else {
        setTasks([]);
      }
    } catch (err) {
      console.error("Error loading tasks:", err);
      setError("Failed to load tasks");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tenant, canSeeAllTasks, session.username, dateFrom, dateTo, statusFilter]);

  useEffect(() => {
    if (tenant) fetchTasks();
  }, [tenant, fetchTasks]);

  // Team workload (managers only), loaded when the Team tab is opened.
  const fetchWorkload = useCallback(async () => {
    if (!tenant || !isManagerOrAbove) return;
    setWorkloadLoading(true);
    try {
      const cfg = sessionRequestConfig();
      const res = await axios.get(`${baseUrl}/api/events/${tenant}/tasks/workload`, cfg);
      if (res.data?.success) setWorkload(res.data.workload || []);
    } catch (err) {
      console.error("Error loading workload:", err);
    } finally {
      setWorkloadLoading(false);
    }
  }, [tenant, isManagerOrAbove]);

  useEffect(() => {
    if (view === "team") fetchWorkload();
  }, [view, fetchWorkload]);

  // Load the current calendar-feed token (if any) once.
  useEffect(() => {
    if (!tenant) return;
    (async () => {
      try {
        const cfg = sessionRequestConfig();
        const res = await axios.get(`${baseUrl}/api/tenants/${tenant}/calendar-feed/token`, cfg);
        if (res.data?.success && res.data.url) setFeed({ url: res.data.url, loading: false });
      } catch { /* not subscribed yet */ }
    })();
  }, [tenant]);

  const enableFeed = async () => {
    setFeed((f) => ({ ...f, loading: true }));
    try {
      const cfg = sessionRequestConfig();
      const res = await axios.post(`${baseUrl}/api/tenants/${tenant}/calendar-feed/token`, {}, cfg);
      if (res.data?.success) setFeed({ url: res.data.url, loading: false });
    } catch (err) {
      console.error("Error enabling calendar feed:", err);
      setFeed((f) => ({ ...f, loading: false }));
    }
  };

  const copyFeed = async () => {
    if (!feed.url) return;
    try {
      await navigator.clipboard.writeText(feed.url);
      setFeedCopied(true);
      setTimeout(() => setFeedCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const exportCsv = () => {
    const params = new URLSearchParams({ format: "csv", range: "year" });
    if (!canSeeAllTasks && session.username) params.set("username", session.username);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    if (statusFilter) params.set("status", statusFilter);
    if (session.tenantId) params.set("tenantId", String(session.tenantId));
    window.open(`${baseUrl}/api/events/${tenant}/tasks?${params.toString()}`, "_blank");
  };

  const clearFilters = () => {
    setSearchTerm("");
    setDateFrom("");
    setDateTo("");
    setStatusFilter("");
    setPriorityFilter("");
  };

  // Client-side filtering for search term and priority (server handles date + status)
  const filtered = tasks.filter((task) => {
    // Search filter
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const title = (task.title || task.name || "").toLowerCase();
      const comment = (task.comment || "").toLowerCase();
      const assignee = (task.assignedToName || "").toLowerCase();
      const eventName = (task.eventName || "").toLowerCase();
      if (!title.includes(q) && !comment.includes(q) && !assignee.includes(q) && !eventName.includes(q)) {
        return false;
      }
    }
    // Priority filter (client-side)
    if (priorityFilter && (task.priority || "").toLowerCase() !== priorityFilter) {
      return false;
    }
    return true;
  });

  const handleTaskClick = (task) => {
    if (task.eventid) {
      const path = task.eventitemid
        ? `/${tenant}/${task.eventid}/${task.eventitemid}/eventItemDetail`
        : `/${tenant}/${task.eventid}/eventDetail`;
      navigate(path);
    }
  };

  const renderTaskItem = (task) => (
    <li
      key={task.id}
      className="list-group-item"
      data-priority={(task.priority || "medium").toLowerCase()}
      onClick={() => handleTaskClick(task)}
      style={{ cursor: task.eventid ? "pointer" : "default" }}
    >
      <div className="tasklist-item-row">
        <span className="tasklist-item-title">
          {task.title || task.name || "Untitled Task"}
          {Number(task.childCount) > 0 && (
            <span className="tasklist-subtask-badge">
              {" "}{Number(task.childDone || 0)}/{Number(task.childCount)}
            </span>
          )}
        </span>
        <span className={`badge ${priorityBadgeClass(task.priority)}`}>
          {(task.priority || "medium").toUpperCase()}
        </span>
      </div>
      {task.eventName && <div className="tasklist-item-event">{task.eventName}</div>}
      <div className="tasklist-item-row tasklist-item-meta">
        <span className="tasklist-item-date">
          {formatDate(task.duedatetime || task.taskdate)}
          {canSeeAllTasks && task.assignedToName && (
            <span className="tasklist-item-assignee">{" · "}{task.assignedToName}</span>
          )}
        </span>
        <span className={`badge ${statusBadgeClass(task.status)}`}>
          {(task.status || "pending").toUpperCase()}
        </span>
      </div>
      {task.comment && (
        <div className="tasklist-item-comment">
          {task.comment.length > 80 ? task.comment.substring(0, 80) + "…" : task.comment}
        </div>
      )}
    </li>
  );

  const AGENDA_GROUPS = [
    { key: "overdue", label: t("taskList.overdue", "Overdue") },
    { key: "today", label: t("taskList.today", "Today") },
    { key: "week", label: t("taskList.thisWeek", "This week") },
    { key: "later", label: t("taskList.later", "Later") },
  ];

  return (
    <div className="tasklist-page tasklist-theme">
      {/* Header */}
      <div className="tasklist-header">
        <h2 className="hdr hdr--primary hdr--lg" style={{ display: "block", margin: 0, color: "#000" }}>
          {canSeeAllTasks
            ? t("taskList.allTasksTitle", "All Tasks")
            : t("taskList.myTasksTitle", "My Tasks")}
        </h2>
        <RefreshButton onClick={() => fetchTasks(true)} loading={refreshing} />
      </div>

      {/* View tabs */}
      <div className="tasklist-tabs">
        <button
          type="button"
          className={`tasklist-tab ${view === "list" ? "is-active" : ""}`}
          onClick={() => setView("list")}
        >
          {t("taskList.tabList", "List")}
        </button>
        <button
          type="button"
          className={`tasklist-tab ${view === "agenda" ? "is-active" : ""}`}
          onClick={() => setView("agenda")}
        >
          {t("taskList.tabAgenda", "Agenda")}
        </button>
        {isManagerOrAbove && (
          <button
            type="button"
            className={`tasklist-tab ${view === "team" ? "is-active" : ""}`}
            onClick={() => setView("team")}
          >
            {t("taskList.tabTeam", "Team")}
          </button>
        )}
      </div>

      {/* Toolbar: subscribe + export */}
      <div className="tasklist-toolbar">
        {feed.url ? (
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={copyFeed} title={feed.url}>
            <FaCopy /> {feedCopied ? t("common.copied", "Copied!") : t("calendarFeed.copyLink", "Copy calendar link")}
          </button>
        ) : (
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={enableFeed} disabled={feed.loading}>
            <FaCalendarAlt /> {feed.loading ? "…" : t("calendarFeed.subscribe", "Subscribe to calendar")}
          </button>
        )}
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={exportCsv}>
          <FaFileCsv /> {t("common.exportCsv", "Export CSV")}
        </button>
      </div>

      {/* Task count */}
      {view !== "team" && (
        <div className="tasklist-count">
          {!loading && (
            <small>
              {filtered.length} {filtered.length === 1 ? "task" : "tasks"}
              {filtered.length !== tasks.length && ` (${tasks.length} total)`}
            </small>
          )}
        </div>
      )}

      {view !== "team" && (
        <>
          {/* Search & Date filter */}
          <SearchFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            onClear={clearFilters}
            searchPlaceholder="Search tasks..."
          />

          {/* Status & Priority filter row */}
          <div className="tasklist-filter-row">
            <select
              className="form-select form-select-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {/* Loading */}
      {loading && view !== "team" && (
        <div className="tasklist-empty">
          <p>{t("common.loading", "Loading...")}</p>
        </div>
      )}

      {/* Error */}
      {error && !loading && view !== "team" && (
        <div className="tasklist-empty">
          <p style={{ color: "#dc3545" }}>{error}</p>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && view !== "team" && filtered.length === 0 && (
        <div className="tasklist-empty">
          <p>{t("taskList.noTasks", "No tasks found")}</p>
        </div>
      )}

      {/* List view */}
      {!loading && !error && view === "list" && filtered.length > 0 && (
        <ul className="list-group tasklist-items">
          {filtered.map((task) => renderTaskItem(task))}
        </ul>
      )}

      {/* Agenda view \u2014 grouped by due-date bucket */}
      {!loading && !error && view === "agenda" && filtered.length > 0 && (
        <div className="tasklist-agenda">
          {AGENDA_GROUPS.map(({ key, label }) => {
            const group = filtered.filter((task) => agendaBucket(task) === key);
            if (group.length === 0) return null;
            return (
              <div key={key} className={`tasklist-agenda-group tasklist-agenda-${key}`}>
                <div className="tasklist-agenda-heading">
                  {label} <span className="tasklist-agenda-count">{group.length}</span>
                </div>
                <ul className="list-group tasklist-items">
                  {group.map((task) => renderTaskItem(task))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Team view \u2014 per-assignee workload */}
      {view === "team" && (
        <div className="tasklist-team">
          {workloadLoading && <div className="tasklist-empty"><p>{t("common.loading", "Loading...")}</p></div>}
          {!workloadLoading && workload.length === 0 && (
            <div className="tasklist-empty"><p>{t("taskWorkload.empty", "No assigned tasks yet")}</p></div>
          )}
          {!workloadLoading && workload.length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm tasklist-team-table">
                <thead>
                  <tr>
                    <th>{t("taskWorkload.person", "Person")}</th>
                    <th title={t("taskWorkload.open", "Open")}>{t("taskWorkload.openShort", "Open")}</th>
                    <th title={t("taskWorkload.overdue", "Overdue")}>{t("taskWorkload.overdueShort", "Late")}</th>
                    <th title={t("taskWorkload.dueToday", "Due today")}>{t("taskWorkload.todayShort", "Today")}</th>
                    <th title={t("taskWorkload.dueThisWeek", "Due this week")}>{t("taskWorkload.weekShort", "Week")}</th>
                    <th title={t("taskWorkload.completed7d", "Completed in the last 7 days")}>{t("taskWorkload.doneShort", "Done 7d")}</th>
                  </tr>
                </thead>
                <tbody>
                  {workload.map((w) => (
                    <tr key={w.userId}>
                      <td>{w.name}</td>
                      <td>{w.open}</td>
                      <td className={w.overdue > 0 ? "text-danger fw-bold" : ""}>{w.overdue}</td>
                      <td>{w.dueToday}</td>
                      <td>{w.dueThisWeek}</td>
                      <td>{w.completed7d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: t("common.back", "Back"),
            onClick: () => navigate(-1),
          },
          {
            icon: <FaHome />,
            label: t("common.home", "Home"),
            onClick: () => navigate(`/${tenant}/dashboard`),
          },
        ]}
      />
    </div>
  );
}
